// client 端登录逻辑（独立模块，便于维护）
// 仅密码登录；token 默认写入 localStorage（记住登录），取消勾选则写入 sessionStorage（关页即清）
// P44：登录成功后自动探明「角色 / 部门 / 数据范围」，供 H5 按角色分流（市场端 / 财务端 / 销售端）
import { getAccountByPassword } from '@/api/login.js'
import { suppressGlobalError } from '@/util/request.js'

const TOKEN_KEY = 'access_token'
const ENTERPRISE_KEY = 'enterprise'
// 角色画像相关键（P44 新增）
const PROFILE_KEYS = [
  'profile',       // 原始画像 JSON（后端 myProfile 返回体）
  'app_scope',     // 'market' | 'finance' | 'crm'（用于登录后分流）
  'role_id',
  'role_name',
  'staff_id',
  'staff_name',
  'dept_ids',      // JSON 数组
  'data_scope',
  'write_scope'
]

// persistent=true → localStorage（跨会话）；false → sessionStorage（关页即清）
function writeKey(key, val, persistent) {
  if (persistent) {
    localStorage.setItem(key, val)
  } else {
    sessionStorage.setItem(key, val)
  }
}
function readKey(key) {
  return localStorage.getItem(key) || sessionStorage.getItem(key)
}
function delKey(key) {
  localStorage.removeItem(key)
  sessionStorage.removeItem(key)
}

export function setToken(token, persistent = true) {
  writeKey(TOKEN_KEY, token, persistent)
}

export function getToken() {
  return readKey(TOKEN_KEY)
}

export function isLoggedIn() {
  return !!readKey(TOKEN_KEY)
}

export function clearAuth() {
  delKey(TOKEN_KEY)
  delKey(ENTERPRISE_KEY)
  PROFILE_KEYS.forEach(k => delKey(k))
}

// 读取已缓存的角色画像（页面直接同步使用，无需等接口）
export function getProfile() {
  const raw = readKey('profile')
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

/* 【P210】页面级刷新画像后回写缓存。
   —— 登录期 persistProfile 只在「登录那一刻」执行；若那一刻网络抖动/token 临期，
      缓存就是空的，之后无论进多少次页面头部都读不到姓名（表现：只显示「销售人员」）。
      本函数把任意一次 myProfile 返回体合并进 profile 缓存，实现「进页面即自愈」。
   ★ 只动 profile / staff_name / role_name / staff_id 四个键，
     绝不碰 app_scope / data_scope / write_scope —— 那些是登录分流键，误写会把端判错。 */
export function cacheProfile(profileLike) {
  if (!profileLike || typeof profileLike !== 'object') { return null }
  const prev = getProfile() || {}
  const next = Object.assign({}, prev, profileLike)
  /* 兼容两种字段名：接口原样返回 name / role_names，归一化缓存是 staff_name / role_name */
  if (!next.staff_name && next.name) { next.staff_name = String(next.name) }
  if (!next.role_name && Array.isArray(next.role_names) && next.role_names.length) {
    next.role_name = next.role_names[0]
  }
  try {
    writeKey('profile', JSON.stringify(next), true)
    if (next.staff_name) { writeKey('staff_name', String(next.staff_name), true) }
    if (next.role_name) { writeKey('role_name', String(next.role_name), true) }
    if (next.staff_id) { writeKey('staff_id', String(next.staff_id), true) }
  } catch (e) { /* 隐私模式等写入失败：忽略，内存态仍然正确 */ }
  return next
}

// 当前端标识：'market' | 'finance' | 'crm'
export function getAppScope() {
  const scope = readKey('app_scope')
  return scope || 'crm'
}

// 退出登录（兜底：token 失效等场景），仍回到保留的 /login 页面
/* ============================================================
 * 【P118】退出登录后的落点：回到「本端自己的登录页」，而不是统统一刀切去 #/login
 *   销售（crm）  → #/client   ← 需求原文：全站 H5 退出后跳 client，不要跳 login
 *   市场（market）→ #/market   ← 需求原文：市场部是在 market 的登录页面
 *   财务 / 运营   → #/finance / #/operation（同理，各回各的端）
 * 判据优先级：显式入参 > 当前 URL 所在端 > 缓存里的 app_scope > 兜底 crm
 * ============================================================ */
const LOGIN_HOME = { crm: '#/client', market: '#/market', finance: '#/finance', operation: '#/operation' }

/** 从当前 hash 判断「现在在哪个端」（认不出返回空串） */
export function scopeFromHash(hash) {
  const h = hash || (typeof window !== 'undefined' ? window.location.hash : '') || ''
  if (h.indexOf('#/market') === 0) { return 'market' }
  if (h.indexOf('#/finance') === 0) { return 'finance' }
  if (h.indexOf('#/operation') === 0) { return 'operation' }
  if (h.indexOf('#/client') === 0) { return 'crm' }
  return ''
}

/** 本端登录页 hash（兜底 #/client） */
export function loginHomePath(targetScope) {
  const scope = targetScope || scopeFromHash() || getAppScope() || 'crm'
  return LOGIN_HOME[scope] || LOGIN_HOME.crm
}

/* 【P118】统一出口：强制「整页加载」到本端登录页
   ⚠️ 坑：直接写 `location.href = './#/client'` 时，如果当前 hash 已经是 #/client，
   浏览器认为 URL 没变 → **不重载**；即便 hash 变了也只是同文档导航（不重载），
   上一会话的内存态会残留 → 用户看到「点了退出但界面没变」。
   所以把时间戳放在 hash **之前**，让浏览器按新 URL 真正重新加载。 */
export function logout(targetScope) {
  const target = loginHomePath(targetScope)
  clearAuth()
  const base = window.location.pathname + String(window.location.search || '').replace(/[?&]_out=\d+/g, '')
  const sep = base.indexOf('?') === -1 ? '?' : '&'
  window.location.href = base + sep + '_out=' + Date.now() + target
}

// ---- 后端字段名兼容取值（不同控制器可能用 id / role_id 等命名） ----
function pick(obj, keys) {
  if (!obj) return ''
  for (let i = 0; i < keys.length; i++) {
    const v = obj[keys[i]]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return ''
}

// 取数组首元素（后端返回的是 role_ids / role_names / dept_ids 这类数组）
function first(arr) {
  return Array.isArray(arr) ? (arr.length ? arr[0] : '') : (arr || '')
}

// 把 myProfile 返回体规范化后落库
// 【实测契约 P44】GET /market/myProfile → data:
//   { staff_id, name, staff_sn, role_ids:[239], dept_ids:[11,12], data_scope:"all",
//     write_scope:"self_created", role_names:["市场人员"], dept_names:["市场部门","销售部"],
//     can:{view_all,create_self,edit_self,batch_assign,view_stat,edit_others,delete_others} }
//   GET /finance/myProfile 额外带 pending_todo + can:{view_all,approve,reject,edit,delete}
function persistProfile(payload, scope, persistent) {
  if (!payload || typeof payload !== 'object') return null
  const d = payload.data && typeof payload.data === 'object' ? payload.data : payload

  const deptRaw = pick(d, ['dept_ids', 'deptIds', 'department_ids'])
  const deptIds = Array.isArray(deptRaw) ? deptRaw : (deptRaw ? [deptRaw] : [])
  const roleIds = (() => {
    const r = pick(d, ['role_ids', 'roleIds', 'role_id', 'roleId'])
    return Array.isArray(r) ? r : (r ? [r] : [])
  })()

  const norm = {
    app_scope: scope,
    staff_id: pick(d, ['staff_id', 'staffId', 'id']),
    staff_sn: pick(d, ['staff_sn', 'staffSn']),
    staff_name: pick(d, ['name', 'staff_name', 'staffName', 'real_name']),
    role_id: first(roleIds),
    role_ids: roleIds,
    role_name: first(pick(d, ['role_names', 'roleNames', 'role_name', 'roleName'])),
    dept_ids: deptIds,
    dept_names: (() => {
      const n = pick(d, ['dept_names', 'deptNames'])
      return Array.isArray(n) ? n : []
    })(),
    data_scope: pick(d, ['data_scope', 'dataScope']),
    write_scope: pick(d, ['write_scope', 'writeScope']),
    pending_todo: pick(d, ['pending_todo', 'pendingTodo']),
    can: (d.can && typeof d.can === 'object') ? d.can : {},
    raw: d
  }

  writeKey('profile', JSON.stringify(norm), persistent)
  writeKey('app_scope', scope, persistent)
  writeKey('role_id', String(norm.role_id || ''), persistent)
  writeKey('role_name', String(norm.role_name || ''), persistent)
  writeKey('staff_id', String(norm.staff_id || ''), persistent)
  writeKey('staff_name', String(norm.staff_name || ''), persistent)
  writeKey('dept_ids', JSON.stringify(norm.dept_ids), persistent)
  writeKey('data_scope', String(norm.data_scope || ''), persistent)
  writeKey('write_scope', String(norm.write_scope || ''), persistent)
  return norm
}

/**
 * 探明当前登录账号的角色画像。
 * 市场端与财务端是两个独立应用、各自有 myProfile；销售/其它角色访问两者都会返回
 * 【HTTP 200 + body code=403】（重要：权限失败不是 HTTP 状态码，而是 body 里的 code）。
 * 因此按顺序试，任一返回 code===200 即认定为该端；两边都失败则认作普通销售端（crm），
 * 不写 app_scope 画像——不影响老流程。
 *
 * 顺序优化：若已知当前端（页面传入 prefer，或缓存里已有 app_scope），先探该端，
 * 避免财务用户每次进财务端都白挨一次 market 的 403。
 *
 * 注意：① 所有失败都必须吞掉，绝不能因为画像探测失败而阻断登录；
 *      ② 必须调用 suppressGlobalError()——request.js 的响应拦截器对 code!=200 会弹
 *         全局红色 Message（「无市场人员权限，请联系管理员」），探测是内部行为，
 *         不该打扰用户。
 */
export function fetchProfile(persistent = true, prefer = null) {
  /* 【P158 修复】hint 必须取「缓存里**真实存在**的 app_scope」，不能用 getAppScope()！
     坑：getAppScope() 在无缓存时兜底返回 'crm'（L68-71），于是 hint 恒为有效值
     'crm' ⇒ finalOrder 把 crm 提到第一位 ⇒ 第一段就 200 命中并 persistProfile
     ⇒ market / finance / operation 三段**永远不会被探测**。
     症状：财务（role 241）/ 市场（239）/ 运营（240）账号登录后 app_scope='crm'，
     被推到销售端或 PC 端（实测：财务 张明华 13918823021 落到 #/client）。
     改为读裸值：无缓存 → hint 为空 → 走原顺序 market→finance→operation→crm，
     财务仍能在第二段命中；已有缓存 → 该端排最前，少挨两次 403。 */
  const cachedScope = (() => {
    try { return localStorage.getItem('app_scope') || sessionStorage.getItem('app_scope') || '' } catch (e) { return '' }
  })()
  const hint = prefer || cachedScope
  /* 【P93】三端探测链：market / finance / operation。
     【P139】追加 crm 终段：原三步对「纯销售角色（237 销售负责人 / 238 销售）」全部 403，
     persistProfile 从不执行 ⇒ localStorage 无 staff_name / role_name / dept ⇒
     快捷入口头部显示不出「销售人员名称」。client/myProfile（P114 已存在）正是为此提供，
     放到最后一段即可保证任何已登录账号都能落一份画像（app_scope='crm'），且不影响前三端命中。
     hint 命中（页面传入 prefer 或缓存已有 app_scope）时把它排到最前，
     避免该端用户每次登录都白挨两次 403。 */
  const ALL = ['market', 'finance', 'operation', 'crm']
  const finalOrder = ALL.indexOf(hint) === -1
    ? ALL
    : [hint].concat(ALL.filter(s => s !== hint))
  let chain = Promise.resolve(null)
  finalOrder.forEach(scope => {
    chain = chain.then(ok => ok || probe(scope, persistent))
  })
  return chain.catch(() => null)
}

/* 【P139】四端 myProfile 取函数表。
   末段 crm 走已存在的 getMyProfileData → POST client/myProfile（P114），
   返回 staff_id / name / staff_sn / role_ids / dept_ids / role_names / dept_names，
   与 persistProfile 的取值口径一致。 */
const PROBE_LOADERS = {
  market:    () => import('../api/market.js').then(m => m.marketMyProfile),
  finance:   () => import('../api/finance.js').then(m => m.financeMyProfile),
  operation: () => import('../api/api.js').then(m => m.operationMyProfile),
  crm:       () => import('../api/api.js').then(m => m.getMyProfileData)
}

// 内部：对指定端发起一次 myProfile，命中即落库
function probe(scope, persistent) {
  const load = PROBE_LOADERS[scope]
  if (!load) return Promise.resolve(null)
  // 请求前的兜底抑制（覆盖「上一轮已置位、本轮响应很快」之外的场景）
  suppressGlobalError()
  return load()
    .then(fn => fn({}))
    .then(res => {
      // 拦截器里的红色提示是在 setTimeout(0) 的宏任务里发的，而这里是微任务，
      // 先于它执行 —— 此处置位即可精确抑制这一次探测的报错弹窗。
      suppressGlobalError()
      if (res && res.code === 200) {
        return persistProfile(res, scope, persistent !== false)
      }
      return null
    })
    .catch(() => {
      suppressGlobalError()
      return null
    })
}

// 密码登录：成功写入 token 与 enterprise 标记，并异步探明角色画像，返回后端 payload
// payload.code === 200 时 response.data 即为 token 字符串
// remember=true（默认）持久化到 localStorage；false 仅当前会话
export function clientPasswordLogin({ phone, password, remember = true }) {
  return getAccountByPassword({ phone, password }).then(response => {
    if (response && response.code === 200) {
      /* 【P158 修复】登录成功 = 换人，必须先清掉上一位登录者残留的画像。
         否则旧 app_scope（如上一任销售留下的 'crm'）会成为 fetchProfile 的 hint，
         把 crm 提到探测链首位 ⇒ 财务/市场/运营账号再次被判成销售端。
         只清画像键，不动 token（token 紧接着就要写入）。 */
      PROFILE_KEYS.forEach(k => delKey(k))
      const persistent = remember !== false
      setToken(response.data, persistent)
      writeKey(ENTERPRISE_KEY, 0, persistent)
    }
    return response
  })
}

// 登录后分流路径：市场 → /market，财务 → /finance，运营 → /operation，其它 → /client
export function homePathByScope() {
  const scope = getAppScope()
  if (scope === 'market') return '/market'
  if (scope === 'finance') return '/finance'
  if (scope === 'operation') return '/operation'
  return '/client'
}



// WEBPACK FOOTER //
// ./src/util/clientAuth.js